/**
 * CORE-BI — ONE table of shop-culture defaults (browser copy).
 *
 * MUST MATCH `supabase/functions/_shared/shopCultureDefaults.ts` — pinned by
 * `tests/unit/core100/shopCultureDefaultsParity.test.ts`.
 *
 * Why this exists (R4 audit G6, CORE-BI T/D): every consumer of
 * `shop_profile.shop_culture` applied its OWN fallback for a key the shop had
 * not stored. The same unset key meant 2 in smart-assign, 3 on the form, 5 in
 * optimize-board, 15 in the proficiency matcher and 50 in the autonomous
 * optimizer — five competing truths for one preference. Several used `||`,
 * which also turned a shop's deliberate 0 into the fallback (a comeback nudge
 * of 0 became 100). A behaviour run by a value the shop cannot see is a hidden
 * policy (CORE-BI l.47).
 *
 * The rule: a consumer reads a culture key ONLY through `cultureNumber` /
 * `cultureBoolean`. A stored finite number (including 0) / boolean is the
 * shop's choice and is used as stored; anything else (missing, null, a string,
 * NaN) resolves to THIS table, which is also what the Shop Profile form shows
 * for an unset key — so what the shop sees is what runs.
 *
 * Values where consumers disagreed were settled to the LEAST permissive of the
 * candidates (Core-100 R4 rule for a missing owner decision; recorded as an
 * owner question): `willingness_roll_past_eod` 0 (no overrun),
 * `present_vehicle_priority` 0 (no automatic preference), `min_split_minutes`
 * 30 (fewer, larger splits). `specialist_gap_splitting` is 3, the current
 * product default (Batch 14) that the form already showed — OWNER to confirm
 * 3 rather than the Nov-2025 migration's 5.
 *
 * Purity: no I/O.
 */

export const SHOP_CULTURE_DEFAULTS = Object.freeze({
  willingness_to_reassign: 50,
  specialist_gap_splitting: 3,
  workload_balance_preference: 0.5,
  comeback_nudge: 100,
  willingness_roll_past_eod: 0,
  present_vehicle_priority: 0,
  ticket_splitting_enabled: true,
  min_split_minutes: 30,
  cross_tech_fitness_threshold: 20,
  proficiency_gap_threshold: 40,
  waiter_time_flex_min: 0,
  no_show_grace_threshold_min: 15,
  // oct20e — FD-2026-10-24-A / CORE-BO :74, :387 as amended: future-day pull-forward of physically present, eligible
  // vehicles is PERMITTED by default; a shop may restrict it (an explicit stored false).
  allow_future_day_pull_forward: true,
  eod_risk_threshold_min: 30,
  // Core-100 R5 (CORE-BI l.67 / CORE-I l.121): rank diagnostic work by demonstrated diagnostic competency.
  // OFF = today's ranking (least permissive; the shop opts in).
  diagnostic_weight_enabled: false,
  // oct20e — FD-2026-10-24-A / CORE-BO :47, :59, :93 as amended: Core automatic capacity recovery is the default;
  // false = the shop's manual suggestion/approval mode (every plan is a suggestion a person accepts).
  capacity_recovery_automatic: true,
} as const);

export type ShopCultureDefaultKey = keyof typeof SHOP_CULTURE_DEFAULTS;

type NumericKey = {
  [K in ShopCultureDefaultKey]: (typeof SHOP_CULTURE_DEFAULTS)[K] extends number ? K : never;
}[ShopCultureDefaultKey];
type BooleanKey = {
  [K in ShopCultureDefaultKey]: (typeof SHOP_CULTURE_DEFAULTS)[K] extends boolean ? K : never;
}[ShopCultureDefaultKey];

/**
 * The value a brand-new shop is created with (the `shop_profile.shop_culture`
 * column DEFAULT, migration 20270206020700). These are STORED values — the
 * shop sees and can edit every one of them. Pinned against the live column
 * default by the SQL proof `core_bi_r4_culture_authority`.
 */
export const SHOP_CULTURE_NEW_SHOP_VALUES = Object.freeze({
  comeback_nudge: 100,
  willingness_to_reassign: 50,
  present_vehicle_priority: 25,
  specialist_gap_splitting: 3,
  willingness_roll_past_eod: 15,
  workload_balance_preference: 0.5,
} as const);

/**
 * The server's accepted range for each numeric key (`__shop_culture_range`),
 * also the Shop Profile form's zod bounds.
 */
export const SHOP_CULTURE_RANGES: Readonly<Record<NumericKey, readonly [number, number]>> = Object.freeze({
  willingness_to_reassign: [0, 100],
  specialist_gap_splitting: [0, 10],
  workload_balance_preference: [0, 1],
  comeback_nudge: [0, 100],
  willingness_roll_past_eod: [0, 120],
  present_vehicle_priority: [0, 120],
  min_split_minutes: [15, 120],
  cross_tech_fitness_threshold: [0, 100],
  proficiency_gap_threshold: [0, 100],
  waiter_time_flex_min: [0, 30],
  no_show_grace_threshold_min: [0, 120],
  eod_risk_threshold_min: [0, 120],
});

function asObject(culture: unknown): Record<string, unknown> {
  return typeof culture === 'object' && culture !== null && !Array.isArray(culture)
    ? (culture as Record<string, unknown>)
    : {};
}

/** A numeric culture value: the shop's stored finite number (0 included), else the shared default. */
export function cultureNumber(culture: unknown, key: NumericKey): number {
  const v = asObject(culture)[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : SHOP_CULTURE_DEFAULTS[key];
}

/** A boolean culture value: the shop's stored boolean, else the shared default. */
export function cultureBoolean(culture: unknown, key: BooleanKey): boolean {
  const v = asObject(culture)[key];
  return typeof v === 'boolean' ? v : SHOP_CULTURE_DEFAULTS[key];
}
